
import React, { useRef, useState, useEffect } from "react";
import "./so_invoice_browser_customer.css";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { CustomerDataType as HOCCustomerDataType } from "../../components/hoc_so_invoice";

interface CustomerDataType extends HOCCustomerDataType {
    key: string;
    cs_code: string;
    cs_name: string;
    customer_type: string;
    address_line1: string;
    address_line2: string;
    no_hp1: string;
}

interface InvoiceCustomerBrowserProps {
    onSelectCustomer: (customer: CustomerDataType) => void;
}

type CustomerDataIndex = keyof CustomerDataType;

interface CustomerTypeOption {
    value: string;
    label: string;
}

const InvoiceCustomerBrowser: React.FC<InvoiceCustomerBrowserProps> = ({ onSelectCustomer }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [customerTypeOptions, setCustomerTypeOptions] = useState<CustomerTypeOption[]>([]);
    const [loadingCustomerTypes, setLoadingCustomerTypes] = useState(false);

    const [customers, setCustomers] = useState<CustomerDataType[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchCustomers = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/so-invoice/customers`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data: CustomerDataType[] = await response.json();
                setCustomers(data);
            } catch (error) {
                console.error("Failed to fetch customers:", error);
                setCustomers([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCustomers();
    }, []);

    useEffect(() => {
        const fetchCustomerTypes = async () => {
            setLoadingCustomerTypes(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/so-invoice/customer-types`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data: CustomerTypeOption[] = await response.json();
                setCustomerTypeOptions(data);
            } catch (error) {
                console.error("Failed to fetch customer types:", error);
                setCustomerTypeOptions([]);
            } finally {
                setLoadingCustomerTypes(false);
            }
        };
        fetchCustomerTypes();
    }, []);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: CustomerDataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: CustomerDataIndex): TableColumnType<CustomerDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex).replace(/([A-Z])/g, ' $1').trim()}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (record[dataIndex] || '')
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<CustomerDataType> = [
        {
            title: 'Customer Code',
            dataIndex: 'cs_code',
            key: 'cs_code',
            ...getColumnSearchProps('cs_code'),
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Customer Name',
            dataIndex: 'cs_name',
            key: 'cs_name',
            ...getColumnSearchProps('cs_name'),
            sorter: (a, b) => a.cs_name.localeCompare(b.cs_name),
            sortDirections: ['ascend', 'descend'],
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Customer Type',
            dataIndex: 'customer_type',
            key: 'customer_type',
            ...getColumnSearchProps('customer_type'),
            render: (text: string) => {
                const customerType = customerTypeOptions.find(option => option.value === text);
                return customerType ? customerType.label : text;
            },
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Address 1',
            dataIndex: 'address_line1',
            key: 'address_line1',
            ...getColumnSearchProps('address_line1'),
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Address 2',
            dataIndex: 'address_line2',
            key: 'address_line2',
            ...getColumnSearchProps('address_line2'),
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            ellipsis: true,
        },
        {
            title: 'Phone Number',
            dataIndex: 'no_hp1',
            key: 'no_hp1',
            ...getColumnSearchProps('no_hp1'),
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
    ];

    return (
        <div id="ifd8" style={transformStyle}>
            <div className="gjs-grid-row" id="im8e">
                <div className="gjs-grid-column" id="irdy">
                    <Table<CustomerDataType>
                        columns={columns}
                        dataSource={customers}
                        pagination={{ pageSize: 5 }}
                        loading={loading}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    const customerTypeLabel = customerTypeOptions.find(option => option.value === record.customer_type)?.label || record.customer_type;                                    
                                    const customerDataToSend: HOCCustomerDataType = {
                                        ...record,
                                        customer_type: customerTypeLabel,
                                    };
                                    onSelectCustomer(customerDataToSend);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default InvoiceCustomerBrowser;